import { Local701FormOfPayment } from 'app/calcs/customers/local701/interfaces'
import { AllFormsOfPayment, AllSimplePensionFormsOfPayment, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { Profile } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import { loadProfilePresets } from 'config/701/ETL'
import { ColumnNames } from 'config/701/ETL/interfaces'
import { defaultMarkup } from 'config/defaultMarkup'
import { IBrand, IBrandHelpers, IBrandPensionInfoNote, IPipelineConfig, IRegistrationInput, IWebConfig, IWebServicesConfig } from 'config/interfaces'
import * as _ from 'lodash'

const Pipeline: IPipelineConfig = {
  Pension: {
    name: 'Pension',
    path: '/pension/',
    icon: 'fa fa-fw fa-calculator',
    thumbnailUrl: '/assets/img/pension.jpg',
    sections: [
      {
        name: 'Pension Intro',
        icon: '',
        hidden: true,
        subsection: {
          default: true,
          hidePrevious: true,
          hideNext: true,
          hideNavRoutes: true,
          questionsGroupName: 'PensionIntro'
        }
      },
      {
        name: 'Local 401 Pension',
        icon: 'fa fa-fw fa-usd',
        subsection: { questionsGroupName: 'Local401PensionQuestionsGroup' }
      },
      {
        name: 'Prior Pension',
        icon: 'fa fa-fw fa-usd',
        subsection: { questionsGroupName: 'PriorPensionQuestionsGroup' }
      },
      {
        name: 'Spouse Pension',
        icon: 'fa fa-fw fa-usd',
        subsection: { questionsGroupName: 'SpousePensionQuestionsGroup' },
        needsSpouse: true
      },
      {
        name: 'Summary',
        icon: 'fa fa-fw fa-balance-scale',
        subsection: {
          hidePrevious: true,
          hideNext: true,
          questionsGroupName: 'PensionSummary'
        }
      },
    ]
  },
  SocialSecurity: {
    name: 'Social Security',
    path: '/socialsecurity/',
    icon: 'fa fa-fw fa-calculator',
    thumbnailUrl: '/assets/img/socialsecurity.jpg',
    sections: [
      {
        name: 'SSA Intro',
        icon: '',
        hidden: true,
        subsection: {
          default: true,
          hidePrevious: true,
          hideNext: true,
          hideNavRoutes: true,
          questionsGroupName: 'SSAIntro'
        }
      },
      {
        name: 'SSA Benefits',
        icon: 'fa fa-fw fa-calculator',
        subsection: {
          hidePrevious: true,
          hideNext: true,
          questionsGroupName: 'SSABenefits'
        }
      },
      {
        name: 'Optimize',
        icon: 'fa fa-fw fa-calculator',
        subsection: {
          hidePrevious: true,
          nextSectionRoutePath: '/retirement/0',
          questionsGroupName: 'SSAOptimizeQuestionsGroup'
        }
      }
    ]
  },
  Retirement: {
    name: 'Savings',
    path: '/savings/',
    icon: 'fa fa-fw fa-calculator',
    thumbnailUrl: '/assets/img/retirement.jpg',
    sections: [
      {
        name: 'Retirement Intro',
        icon: '',
        hidden: true,
        subsection: {
          default: true,
          hidePrevious: true,
          hideNext: true,
          hideNavRoutes: true,
          questionsGroupName: 'RetirementIntro'
        }
      },
      {
        name: 'Investment Return',
        icon: 'fa fa-fw fa-calculator',
        subsection: {
          questionsGroupName: 'RetirementInvestmentReturns'
        }
      },
      {
        name: 'Member',
        icon: 'fa fa-fw fa-calculator',
        subsections: [
          { questionsGroupName: 'RetirementMemberContributionsQuestionsGroup' },
          { questionsGroupName: 'RetirementMemberWithdrawalQuestionsGroup' },
        ]
      },
      {
        name: 'Spouse',
        icon: 'fa fa-fw fa-calculator',
        subsections: [
          { questionsGroupName: 'RetirementSpouseContributionsQuestionsGroup' },
          { questionsGroupName: 'RetirementSpouseWithdrawalQuestionsGroup' },
        ],
        needsSpouse: true
      },
      {
        name: 'Summary',
        icon: 'fa fa-fw fa-balance-scale',
        subsection: {
          hidePrevious: true,
          hideNext: true,
          questionsGroupName: 'RetirementSummary'
        }
      },
    ]
  },
  Expenses: {
    name: 'Expenses',
    path: '/expenses/',
    icon: 'fa fa-fw fa-calculator',
    thumbnailUrl: '/assets/img/expenses.jpg',
    sections: [
      {
        name: 'Expenses Intro',
        icon: '',
        hidden: true,
        subsection: {
          default: true,
          hidePrevious: true,
          hideNext: true,
          hideNavRoutes: true,
          questionsGroupName: 'ExpensesIntro'
        }
      },
      {
        name: 'Home Expenses',
        icon: 'fa fa-fw fa-home',
        subsections: [
          { questionsGroupName: 'MortgageQuestionsGroup' },
          { questionsGroupName: 'PropertyTaxesQuestionsGroup' },
          { questionsGroupName: 'HomeInsuranceQuestionsGroup' },
          { questionsGroupName: 'HomeMaintanenceQuestionsGroup' },
          { questionsGroupName: 'UtilitiesQuestionsGroup' },
        ]
      },
      {
        name: 'Auto',
        icon: 'fa fa-fw fa-car',
        subsection: { questionsGroupName: 'AutoQuestionsGroup' },
      },
      {
        name: 'Food',
        icon: 'fa fa-fw fa-cutlery',
        subsections: [
          { questionsGroupName: 'GroceriesQuestionsGroup' },
          { questionsGroupName: 'DiningOutQuestionsGroup' },
        ]
      },
      {
        name: 'Clothing & Personal',
        icon: 'fa fa-fw fa-bath',
        subsections: [
          { questionsGroupName: 'ClothingQuestionsGroup' },
          { questionsGroupName: 'WardrobeQuestionsGroup' },
        ]
      },
      {
        name: 'Entertainment',
        icon: 'fa fa-fw fa-ship',
        subsections: [
          { questionsGroupName: 'TravelQuestionsGroup' },
          { questionsGroupName: 'OtherEntertainmentQuestionsGroup' },
        ]
      },
      {
        name: 'Other',
        icon: 'fa fa-fw fa-briefcase',
        subsections: [
          { questionsGroupName: 'MedicalQuestionsGroup'},
          { questionsGroupName: 'CharityQuestionsGroup' },
          { questionsGroupName: 'TaxesQuestionsGroup' },
          { questionsGroupName: 'SavingsDebtQuestionsGroup' },
        ]
      },
      {
        name: 'Summary',
        icon: 'fa fa-fw fa-balance-scale',
        subsection: {
          hidePrevious: true,
          hideNext: true,
          questionsGroupName: 'ExpensesSummary'
        }
      },
    ]
  },
  Results: {
    name: 'Retirement Summary',
    path: '/Results/',
    icon: 'fa fa-fw fa-heartbeat',
    hiddenInDashboard: true,
    sections: [
      {
        name: 'Retirement Summary',
        icon: '',
        hidden: true,
        subsection: {
          default: true,
          hidePrevious: true,
          hideNext: true,
          hideNavRoutes: true,
          questionsGroupName: 'Results'
        }
      }
    ]
  }
}

const productionServer = window.location.origin + '/'
const productionSSAServer = 'https://ssa2.localpension.com/'
const WebServices: IWebServicesConfig = {
  accountsEndpoint: productionServer,
  familyInfoEndpoint: productionServer,
  ssaWorkbookEndpoint: productionSSAServer,
  ncpersWorkbookEndpoint: productionServer,
  testWorkbookEndpoint: productionServer,
  miscEndpoint: productionServer,
  workbookEndpoint: productionServer
}

// if we're not been run in production, choose our web servers
if (!utils.isProductionEnvironment()) {
  const endpointMap = {
    themajordomo: 'http://local401.themajordomo.com/',
    localpension: 'https://local401.localpension.com/',
    dev: 'http://local401.tjdoldserver.com:8001/'
  }
  const devServer = utils.selectWebservicesEndpoint(endpointMap)
  _.merge(WebServices, {
    accountsEndpoint: devServer,
    familyInfoEndpoint: devServer,
    ssaWorkbookEndpoint: productionSSAServer,
    ncpersWorkbookEndpoint: devServer,
    testWorkbookEndpoint: devServer,
    miscEndpoint: devServer,
    workbookEndpoint: devServer
  })
}

const RegistrationInputs: IRegistrationInput[] = [
  {
    name: 'firstname',
    placeholder: 'First Name',
    type: 'text',
  },
  {
    name: 'person.lastName',
    placeholder: 'Last Name',
    type: 'text',
  },
  {
    name: 'person.uniqueID.numeric',
    placeholder: 'Your member ID',
    type: 'text',
  },
  {
    name: 'username',
    placeholder: 'Username',
    type: 'text',
  },
  {
    name: 'password',
    placeholder: 'Create Password',
    type: 'password',
  },
]

const Brand: IBrand = {
  imgUrl: '/assets/img/Local401-logo-117x117.png',
  brandName: 'Local 401',
  contactPhone: '(215) 555-1234',
  contactEmail: 'info@local401.com',
  landingPageGreeting: 'Welcome to the Ironworkers Local 401 Retirement Planning Calculator',
  introductionVideos: [
    { caption: 'Introduction to the Retirement App', url: 'https://youtube.com/embed/bQeESe_Zqd4' },
    { caption: '(pdf of script)', url: '/content/Local XYZ intro slides_with words 8.1.2018.pdf', suppressLine: true }
  ],
  dashboardVideos: [
  ],
  ssaVideos: [
    { caption: 'Introduction to the Social Security Optimizer', url: 'https://youtube.com/embed/-coRrBRdt2w' },
    { caption: '(pdf of script)', url: '/content/SSA/Intro to the SS optimizer_w notes_7.192018.pdf', suppressLine: true },
    { caption: 'Knowledge Is Power', url: 'https://www.youtube.com/embed/56LudX1hui0' },
    { caption: '(pdf of script)', url: '/content/SSA/SS Knowledge is power_w notes_7.19.2018.pdf', suppressLine: true },
  ],
  savingsMemberTitleSuffix: 'Savings Balance',
  markup: defaultMarkup
}

/**
 * Returns a collection of information about a pension.
 *
 * @param profile - the users profile
 * @param hint - the pension type ("Local701", "NEBF", "IO" etc.)
 */
function getPensionInfoNotes(profile: Profile): IBrandPensionInfoNote[] {
  return pensionInfos
}

function isLockedData(profile: Profile, path: string) {
  const ex = profile.extraInfo;

  if (path === '.fixedBalances.retirementSavings') {
    return !_.isUndefined(ex[ColumnNames.X401KAcctBal])
  }

  return false;
}

const BrandHelpers: IBrandHelpers = {
  loadProfilePresets: (profile) => loadProfilePresets(profile, false, false),
  memberPensionFormsOfPayment: [
    Local701FormOfPayment.lifeOnly,
    Local701FormOfPayment.fiftyPercentHusbandAndWife,
    Local701FormOfPayment.seventyFivePercentHusbandAndwife
  ],
  spousalPensionFormsOfPayment: AllSimplePensionFormsOfPayment,
  getPensionInfoNotes,
  isLockedData,
  hasIncomingSavingsBalance: true,
  showNonCoveredPriorPensionLanguage: false
}

const pensionInfos = [
  { url: '/content/local401/Local 401_SPD.pdf', linkText: 'Local 401 SPD' }
]

const Config401: IWebConfig = {
  GATrackingID: 'G-14BKVC6DMF',
  Pipeline,
  WebServices,
  RegistrationInputs,
  Brand,
  BrandHelpers
}

export {
  Config401
}



// WEBPACK FOOTER //
// ./src/config/401/index.ts